<template>
    <div component="VideoRecorder">
        <div
            class="recorder-message"
            v-if="_message.visible"
            :class="{ error: _message.error, closeable: _message.closeable }"
        >
            <btn
                v-if="_message.closeable"
                class="close"
                color=""
                size="icon"
                :icon="'close'"
                @click.stop="messageClose"
            />
            <div class="recorder-message-content">
                <div class="recorder-message-icons">
                    <i class="icon success" v-if="!_message.error">check_circle</i>
                    <i class="icon error" v-else>error</i>
                </div>
                <div class="recorder-message-message" v-html="_message.message"></div>
            </div>
            <div v-if="_message.buttons && _message.buttons.length" class="recorder-message-button">
                <btn
                    v-for="(btn, index) in _message.buttons"
                    :key="index"
                    tpos="right"
                    :icon="btn.icon"
                    :text="btn.text"
                    @click.prevent="messageButtonClick(btn.click)"
                />
            </div>
        </div>

        <div class="wrapper">
            <div class="video">
                <video ref="video" id="myVideo" playsinline class="video-js vjs-default-skin" />
            </div>
            <video-recorder-subtitles
                v-if="scriptText && isRecordable"
                :script-text="scriptText"
                :is-scrolling="subtitlesIsScrolling"
            />
            <video-recorder-count-down
                v-model="countingDownStart"
                seconds="3"
                color="green"
                text="Recording starts in"
                @end="startRecording"
            />
            <video-recorder-count-down
                v-model="countingDownEnd"
                :seconds="countingDownEndSeconds"
                color="red"
                text="Recording will end in"
                @end="stopRecording"
            />
        </div>

        <div class="remote">
            <div class="buttons">
                <btn
                    v-if="isMobile && !isSubmittable"
                    :disabled="countingDownStart || isRecording"
                    @click.prevent="toggleCamera"
                    text="Flip Camera"
                />
                <btn
                    v-if="!isSubmittable && !isUploading"
                    :color="isRecording ? 'accent' : 'base'"
                    :icon="isRecording ? 'stop' : 'play_circle_outline'"
                    @click.prevent="toggleRecording"
                    :disabled="!isRecordable || (!isRecording && countingDownStart)"
                    :text="isRecording ? 'Stop Recording' : 'Start Recording'"
                />
                <btn
                    v-if="isSubmittable || isUploading"
                    class="submit"
                    tpos="right"
                    :state="isUploading ? 'load' : ''"
                    :icon="'save'"
                    @click.prevent="submit"
                    :text="!isUploading ? 'Submit this video' : 'Processing'"
                />
                <btn
                    v-if="isSubmittable && !isUploading"
                    class="delete"
                    tpos="left"
                    :icon="'close'"
                    color="ghost"
                    @click.prevent="$_setState(states.ready)"
                    :text="'Record new video'"
                />
            </div>
        </div>
    </div>
</template>

<script>
import 'video.js/dist/video-js.css';
import 'videojs-record/dist/css/videojs.record.css';
import DeviceDetector from 'device-detector-js';
import videojs from 'video.js';
import 'webrtc-adapter';
import RecordRTC from 'recordrtc';
import Record from 'videojs-record/dist/videojs.record.js';
import FFmpegjsEngine from 'videojs-record/dist/plugins/videojs.record.ffmpegjs.js';
import Env from '@lib/Env';
import VideoRecorderSubtitles from '@/components/Profile/Video/VideoRecorderSubtitles';
import VideoRecorderCountDown from '@/components/Profile/Video/VideoRecorderCountDown';

export const states = {
    initializing: 'initializing',
    ready: 'ready',
    recording: 'recording',
    recorded: 'recorded',
    uploading: 'uploading',
    error: 'error',
};

const MAX_LENGTH_SECONDS = 180;

const videojsOptions = {
    controls: true,
    bigPlayButton: false,
    controlBar: {
        fullscreenToggle: false,
        deviceButton: false,
        recordToggle: true,
        pipToggle: false,
    },
    width: 500,
    height: 300,
    fluid: true,
    plugins: {
        record: {
            pip: false,
            audio: true,
            video: true,
            maxLength: MAX_LENGTH_SECONDS,
            debug: Env.isDevelopment,
        },
    },
};

const refresh = () => window.location.reload();

export const defaultVrMessageButton = {
    text: '',
    icon: '',
    click: undefined,
};

export const defaultVrMessage = {
    visible: false,
    closeable: true,
    message: '',
    error: false,
    buttons: [], // Use defaultVrMessageButton as default
};

const generalVrError = {
    ...defaultVrMessage,
    visible: true,
    error: true,
    code: 'unknown',
    message: 'Unknown error occured.',
};

const recorderApiNotSupported = {
    ...generalVrError,
    code: 'no_recorder_api',
    message: 'Your browser does not support the Media Recorder API',
    closeable: false,
};

const notSafariOnIOsError = {
    ...generalVrError,
    code: 'not_safari_on_ios',
    message: 'Your browser does not support video recording.<br/>Please use Safari browser.',
    closeable: false,
};

const browserNotSupported = {
    ...generalVrError,
    code: 'browser_not_supported',
    message: 'Your browser does not support video recording.<br/>Please use Chrome browser.',
    closeable: false,
};

const noDeviceError = {
    ...generalVrError,
    code: 'no_device',
    message: 'Recording device not available.<br/>Please connect or enable camera with microphone.',
    closeable: false,
    buttons: [
        {
            ...defaultVrMessageButton,
            text: 'Refresh',
            icon: 'refresh',
            click: refresh,
        },
    ],
};

const permissionDeniedError = {
    ...generalVrError,
    code: 'permission_denied',
    message: 'Please grant permissions to use camera with microphone to record video.',
    closeable: false,
    buttons: [
        {
            ...defaultVrMessageButton,
            text: 'Refresh',
            icon: 'refresh',
            click: refresh,
        },
    ],
};

export default {
    name: 'VideoRecorder',
    components: { VideoRecorderCountDown, VideoRecorderSubtitles },
    props: ['videoRecorderDialog', 'scriptText', 'uploading', 'message'],

    data() {
        return {
            remainingSecondsInterval: null,
            remainingSeconds: MAX_LENGTH_SECONDS,
            state: states.initializing,
            states,
            player: null,
            retake: 0,
            isRetakeDisabled: true,
            subtitlesIsScrolling: false,
            countingDownStart: false,
            countingDownEnd: false,
            countingDownEndSeconds: 10,
            localMessage: {
                ...defaultVrMessage,
            },
            selectedDeviceId: null,
            videoInputs: [],
            device: 0,
        };
    },

    mounted() {
        this.init();
    },

    beforeDestroy() {
        if (this.player) {
            this.player.dispose();
        }
    },

    methods: {
        init() {
            const device = new DeviceDetector().parse(navigator.userAgent);

            this.$env.log.info('Initializing...');

            // VideoJS recording not supported on Chrome on iOS
            // https://collab-project.github.io/videojs-record/#/browser-support
            if (device.os.name == 'iOS' && !/Safari/i.test(device.client.name)) {
                this.localMessage = notSafariOnIOsError;
                return;
            }

            // Recording API check
            if (typeof MediaRecorder === 'undefined' || !window.navigator.mediaDevices.getUserMedia) {
                this.localMessage = recorderApiNotSupported;
                return;
            }

            this.initRecorder();
        },

        filterCameras(devices) {
            const cameras = devices.filter((device) => device.kind === 'videoinput');
            let frontCamera = null;
            let backCamera = null;

            // Pokusíme sa identifikovať prednú a zadnú kameru podľa labelu alebo iných vlastností
            cameras.forEach((camera) => {
                const label = camera.label.toLowerCase();
                if (!frontCamera && (label.includes('front') || label.includes('hd'))) {
                    frontCamera = camera.deviceId;
                } else if (!backCamera && label.includes('back')) {
                    backCamera = camera.deviceId;
                }
            });

            // Ak sme na PC alebo nemáme jednoznačné označenie, predpokladáme, že prvá kamera je primárna
            if (!frontCamera) {
                frontCamera = cameras[0] ? cameras[0].deviceId : null;
            }

            if (!backCamera && cameras.length > 1) {
                backCamera = cameras.find((camera) => camera.deviceId !== frontCamera)?.deviceId || null;
            }

            return [frontCamera, backCamera].filter(Boolean);
        },

        initRecorder() {
            this.$env.log.debug('Initializing recorder');

            if (this.player) {
                return;
            }

            this.player = videojs('myVideo', videojsOptions, () => {
                this.player.record().getDevice();
            });

            this.player.one('deviceReady', () => {
                this.$env.log.debug('Device ready');
                this.player.record().enumerateDevices();
                this.$_setState(states.ready);
            });

            this.player.on('enumerateReady', () => {
                const devices = this.player.record().devices;

                this.videoInputs = this.filterCameras(devices);

                this.player.record().setVideoInput(this.videoInputs[this.device]);
                this.player.play();
            });

            this.player.on('deviceError', (e) => {
                this.$env.log.error(e);
                this.localMessage = this.getError();
                this.$bugsnag.notify(new Error(e));
                this.$_setState(states.error);
            });

            this.player.on('error', (element, e) => {
                this.$env.log.error(e);
                this.localMessage = this.getError();
                this.$bugsnag.notify(new Error(e));
                this.$_setState(states.error);
            });

            this.player.on('startRecord', () => {
                this.$env.log.debug('Start recording');
            });

            this.player.on('finishRecord', () => {
                this.$env.log.debug('Stop recording');
                if (this.retake == 0) {
                    this.isRetakeDisabled = false;
                }
                this.stopRecording();
            });
        },

        toggleRecording() {
            if (!this.isRecording) {
                this.countingDownStart = true;
                this._message.visible = false;
            } else {
                this.stopRecording();
            }
        },

        startRecording() {
            this.$_setState(states.recording);
            this.resetSettings();
            this.retake += 1;
            this.player.record().start();

            setTimeout(() => {
                this.subtitlesIsScrolling = true;
            }, 3000);

            this.remainingSecondsInterval = setInterval(this.checkTime, 1000);
        },

        stopRecording() {
            this.$_setState(states.recorded);
            this.resetSettings();
            this.player.record().stop();
            this.subtitlesIsScrolling = false;
            this.countingDownEnd = false;
            clearInterval(this.remainingSecondsInterval);
        },

        resetSettings() {
            this.isRetakeDisabled = true;
        },

        checkTime() {
            this.remainingSeconds--;
            if (this.remainingSeconds === this.countingDownEndSeconds) {
                clearInterval(this.remainingSecondsInterval);
                this.countingDownEnd = true;
            }
        },

        toggleCamera() {
            if (this.device === 1) {
                this.device = 0;
                this.player.record().setVideoInput(this.videoInputs[this.device]);
            } else {
                this.device = 1;
                this.player.record().setVideoInput(this.videoInputs[this.device]);
            }
        },

        getError(code) {
            code = code || this.player.deviceErrorCode;
            switch (true) {
                case /device not found/.test(code):
                    return noDeviceError;
                case /Could not start video source/.test(code):
                case /videoinput failed/i.test(code):
                    return browserNotSupported;
                case /denied/.test(code):
                    return permissionDeniedError;
                default:
                    return generalVrError;
            }
        },

        messageButtonClick(btnCallback) {
            const bound = btnCallback.bind(this);
            bound(this);
        },

        messageClose() {
            if (this.message?.visible == true) {
                this.$emit('update:message', { ...defaultVrMessage });
            }

            if (this.localMessage?.visible == true) {
                this.localMessage = { ...defaultVrMessage };
            }
        },

        submit() {
            if (!this.isSubmittable) {
                return;
            }
            this.$emit('submit', this.player.recordedData);
        },
    },

    computed: {
        isRecordable() {
            return this.$_stateIs([states.ready, states.recording, states.recorded]);
        },

        isRecording() {
            return this.$_stateIs(states.recording);
        },

        isSubmittable() {
            return this.$_stateIs(states.recorded);
        },

        isUploading() {
            return this.$_stateIs(states.uploading) && this.uploading;
        },

        isMobile() {
            const device = new DeviceDetector().parse(navigator.userAgent);
            return device.device.type === 'smartphone' || device.device.type === 'tablet';
        },

        _message() {
            return this.message?.visible ? this.message : this.localMessage;
        },
    },

    watch: {
        videoRecorderDialog(val) {
            this.resetSettings();
            if (!val) {
                this.player.record().stopDevice();
            }
        },

        message(newVal) {
            if (newVal?.error) {
                this.$_setState(states.error);
            }
        },

        uploading(newVal) {
            if (newVal) {
                return this.$_setState(states.uploading);
            }

            if (this.$_stateIs(states.error)) {
                this.$_setState(states.recorded);
            } else {
                this.$_setState(states.ready);
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '~@variables';

[component='VideoRecorder'] {
    width: 100%;
    height: 100%;

    .wrapper {
        position: relative;
        height: 100%;

        .video {
            height: 100%;
            background: black;

            .video-js {
                padding: initial !important;
                height: 100%;
            }
        }
    }

    .remote {
        width: 100%;
        position: absolute;
        bottom: 40px;
        left: 0;

        .buttons {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: stretch;

            button {
                margin: 5px;
            }
        }
    }

    .recorder-message {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 890;
        width: 100%;
        max-width: 340px;
        text-align: center;
        background: @base;
        border-radius: 10px;
        padding: 15px 20px;
        color: white;
        font-size: 18px;
        font-weight: bold;
        line-height: 1.2;
        display: flex;
        align-items: center;
        flex-direction: column;

        &.closeable {
            .recorder-message-content {
                padding-right: 22px;
            }
        }

        .close {
            position: absolute;
            right: 5px;
            top: 5px;
        }

        .recorder-message-content {
            display: flex;
        }

        .recorder-message-icons {
            padding-right: 20px;
            .flex-center;

            .icon {
                font-size: 30px;

                &.success {
                    background: @base !important;
                }
            }
        }

        .recorder-message-message {
            text-align: left;
        }

        .recorder-message-button {
            width: 100%;
            padding-top: 15px;
        }
    }

    @keyframes slide {
        from {
            transform: translateY(15%);
        }
        to {
            transform: translateY(-100%);
        }
    }
}
</style>
